import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CalendarClock, CheckCircle2, Circle, Plus } from 'lucide-react';
import { projectProgressPercent } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from './Card.tsx';
import { ProjectProgressBar } from './ProjectProgressBar.tsx';
import { QuoteScopePanel } from './QuoteScopePanel.tsx';

// The project, on the ticket the technician works from.
//
// Three things, in the order they are used: how far along it is (the same
// bar the client sees), the checklist - milestones with their tasks, ticked
// off here - and the scope of work that was signed. The checklist is the
// project's, so ticking a box here is ticking it everywhere: the project
// page, the client's portal, the milestone it belongs to.

interface Project {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'cancelled';
  dueDate: string | null;
  quoteTicketId: string | null;
  sowSummary: string | null;
}

interface Milestone {
  id: string;
  title: string;
  milestoneDate: string;
  completedAt: string | null;
}

interface Subtask {
  id: string;
  milestoneId: string | null;
  title: string;
  dueDate: string | null;
  completedAt: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const today = (): string => new Date().toLocaleDateString('en-CA');

export function ProjectWorkPanel({ projectId }: { projectId: string }): React.ReactElement {
  const [project, setProject] = useState<Project | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, m, s] = await Promise.all([
        apiFetch<{ project: Project }>(`/api/projects/${projectId}`),
        apiFetch<{ milestones: Milestone[] }>(`/api/projects/${projectId}/milestones`),
        apiFetch<{ subtasks: Subtask[] }>(`/api/projects/${projectId}/subtasks`),
      ]);
      setProject(p.project);
      setMilestones(m.milestones);
      setSubtasks(s.subtasks);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the project plan.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleTask(task: Subtask): Promise<void> {
    setBusyId(task.id);
    // Ticked at once, so the box answers the click; the reload below
    // brings the server's version back, and puts it back if the save
    // failed.
    setSubtasks((list) =>
      list.map((s) =>
        s.id === task.id ? { ...s, completedAt: task.completedAt ? null : new Date().toISOString() } : s,
      ),
    );
    try {
      await apiFetch(`/api/projects/${projectId}/subtasks/${task.id}/completion`, {
        method: 'POST',
        json: { completed: !task.completedAt },
      });
      // Reload rather than patch: ticking the last task finishes its
      // milestone on the server, and the page should show that.
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update that task.');
      await load();
    } finally {
      setBusyId(null);
    }
  }

  async function toggleMilestone(milestone: Milestone): Promise<void> {
    setBusyId(milestone.id);
    try {
      await apiFetch(`/api/projects/${projectId}/milestones/${milestone.id}`, {
        method: 'PATCH',
        json: { completed: !milestone.completedAt },
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update that milestone.');
    } finally {
      setBusyId(null);
    }
  }

  async function addTask(title: string, milestone: Milestone | null): Promise<void> {
    await apiFetch(`/api/projects/${projectId}/subtasks`, {
      method: 'POST',
      json: {
        title,
        milestoneId: milestone?.id ?? null,
        dueDate: milestone?.milestoneDate ?? null,
      },
    });
    await load();
  }

  if (error && !project) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-danger)]">{error}</p>
      </Card>
    );
  }
  if (!project) {
    return (
      <Card>
        <p className="text-sm text-[var(--color-muted)]">Loading the project plan…</p>
      </Card>
    );
  }

  const done = subtasks.filter((s) => s.completedAt).length;
  const percent = projectProgressPercent({
    status: project.status,
    tasksTotal: subtasks.length,
    tasksDone: done,
    milestonesTotal: milestones.length,
    milestonesDone: milestones.filter((m) => m.completedAt).length,
  });
  const loose = subtasks.filter((s) => !s.milestoneId || !milestones.some((m) => m.id === s.milestoneId));

  return (
    <>
      <Card>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Project plan</h2>
            <p className="text-sm text-[var(--color-muted)]">
              {project.dueDate ? (
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" /> Due{' '}
                  {fmtDate(project.dueDate)}
                </span>
              ) : (
                'No due date set'
              )}
              {' · '}The client sees this progress on their portal.
            </p>
          </div>
          <Link
            to={`/projects/${project.id}`}
            className="text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            Open project →
          </Link>
        </div>

        <ProjectProgressBar percent={percent} done={done} total={subtasks.length} />

        {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

        {milestones.length === 0 && subtasks.length === 0 ? (
          <p className="mt-4 rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-white px-4 py-4 text-sm text-[var(--color-muted)]">
            No milestones or tasks yet. Add them on the project, or add a task here.
          </p>
        ) : (
          <div className="mt-5 space-y-5">
            {milestones.map((milestone) => {
              const tasks = subtasks.filter((s) => s.milestoneId === milestone.id);
              const overdue = !milestone.completedAt && milestone.milestoneDate < today();
              return (
                <section key={milestone.id}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--color-border)] pb-1.5">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-header)]">
                      {tasks.length === 0 ? (
                        // A milestone with no tasks is ticked by hand.
                        <button
                          type="button"
                          onClick={() => void toggleMilestone(milestone)}
                          disabled={busyId === milestone.id}
                          aria-pressed={Boolean(milestone.completedAt)}
                          aria-label={`Mark ${milestone.title} ${milestone.completedAt ? 'not done' : 'done'}`}
                        >
                          {milestone.completedAt ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                          ) : (
                            <Circle className="h-4 w-4 text-[var(--color-muted)]" aria-hidden="true" />
                          )}
                        </button>
                      ) : milestone.completedAt ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                      ) : null}
                      {milestone.title}
                    </h3>
                    <span
                      className={`text-xs ${overdue ? 'font-semibold text-amber-700' : 'text-[var(--color-muted)]'}`}
                    >
                      {overdue ? 'Overdue · ' : ''}
                      {fmtDate(milestone.milestoneDate)}
                    </span>
                  </div>
                  <TaskList tasks={tasks} busyId={busyId} onToggle={toggleTask} />
                  <AddTask onAdd={(title) => addTask(title, milestone)} label={milestone.title} />
                </section>
              );
            })}
            {loose.length > 0 && (
              <section>
                <h3 className="border-b border-[var(--color-border)] pb-1.5 text-sm font-semibold text-[var(--color-header)]">
                  Other tasks
                </h3>
                <TaskList tasks={loose} busyId={busyId} onToggle={toggleTask} />
              </section>
            )}
          </div>
        )}
        {milestones.length === 0 && <AddTask onAdd={(title) => addTask(title, null)} label="the project" />}
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Scope of work</h2>
        <p className="mt-1 mb-4 text-sm text-[var(--color-muted)]">What the client signed for.</p>
        {project.quoteTicketId ? (
          <QuoteScopePanel ticketId={project.quoteTicketId} />
        ) : project.sowSummary ? (
          <p className="whitespace-pre-wrap text-sm text-[var(--color-body)]">{project.sowSummary}</p>
        ) : (
          <p className="text-sm text-[var(--color-muted)]">
            No scope of work recorded. It can be added on the project.
          </p>
        )}
      </Card>
    </>
  );
}

function TaskList({
  tasks,
  busyId,
  onToggle,
}: {
  tasks: Subtask[];
  busyId: string | null;
  onToggle: (task: Subtask) => void | Promise<void>;
}): React.ReactElement | null {
  if (tasks.length === 0) return null;
  return (
    <ul className="mt-1">
      {tasks.map((task) => (
        <li key={task.id}>
          <label className="flex cursor-pointer items-start gap-3 rounded px-1 py-1.5 hover:bg-[var(--color-surface)]">
            <input
              type="checkbox"
              checked={Boolean(task.completedAt)}
              disabled={busyId === task.id}
              onChange={() => void onToggle(task)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-t3-blue)]"
            />
            <span
              className={`flex-1 text-sm ${
                task.completedAt ? 'text-[var(--color-muted)] line-through' : 'text-[var(--color-body)]'
              }`}
            >
              {task.title}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

function AddTask({
  onAdd,
  label,
}: {
  onAdd: (title: string) => Promise<void>;
  label: string;
}): React.ReactElement {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="mt-1 flex items-center gap-2 pl-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setBusy(true);
        setError(null);
        onAdd(title.trim())
          .then(() => setTitle(''))
          .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not add it.'))
          .finally(() => setBusy(false));
      }}
    >
      <Plus className="h-3.5 w-3.5 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        disabled={busy}
        placeholder="Add a task"
        aria-label={`Add a task to ${label}`}
        className="min-w-0 flex-1 border-0 bg-transparent py-1 text-sm text-[var(--color-body)] outline-none placeholder:text-[var(--color-muted)]"
      />
      {error && <span className="text-xs text-[var(--color-danger)]">{error}</span>}
    </form>
  );
}
